// Phase 2: Signing Key Vault (IndexedDB-backed)
// Hardened with timeouts, onblocked/onversionchange, awaited transactions.

const DB_NAME = 'whispai-signing-vault';
const STORE_NAME = 'signing-keys';
const OPEN_TIMEOUT_MS = 4000;
const TX_TIMEOUT_MS = 4000;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => { if (!settled) { settled = true; fn(); } };
    const timer = setTimeout(() => {
      console.warn('[signingKeyVault] open timed out');
      finish(() => reject(new Error('signingKeyVault: open timeout')));
    }, OPEN_TIMEOUT_MS);

    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, 1);
    } catch (e) {
      clearTimeout(timer);
      finish(() => reject(e));
      return;
    }

    request.onupgradeneeded = () => {
      try {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      } catch (e) {
        console.warn('[signingKeyVault] upgrade failed', e);
      }
    };
    request.onblocked = () => {
      console.warn('[signingKeyVault] open blocked');
      clearTimeout(timer);
      finish(() => reject(new Error('signingKeyVault: open blocked')));
    };
    request.onsuccess = () => {
      clearTimeout(timer);
      const db = request.result;
      db.onversionchange = () => { try { db.close(); } catch { /* ignore */ } };
      finish(() => resolve(db));
    };
    request.onerror = () => {
      clearTimeout(timer);
      finish(() => reject(request.error ?? new Error('signingKeyVault: open error')));
    };
  });
}

function awaitTx(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => { if (!settled) { settled = true; fn(); } };
    const timer = setTimeout(() => {
      console.warn('[signingKeyVault] tx timed out');
      try { tx.abort(); } catch { /* ignore */ }
      finish(() => reject(new Error('signingKeyVault: tx timeout')));
    }, TX_TIMEOUT_MS);
    tx.oncomplete = () => { clearTimeout(timer); finish(() => resolve()); };
    tx.onabort = () => { clearTimeout(timer); finish(() => reject(tx.error ?? new Error('tx abort'))); };
    tx.onerror = () => { clearTimeout(timer); finish(() => reject(tx.error ?? new Error('tx error'))); };
  });
}

export async function saveSigningPrivateKey(userId: string, key: CryptoKey) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(key, `signing-private:${userId}`);
    await awaitTx(tx);
  } catch (e) {
    console.warn('[signingKeyVault] saveSigningPrivateKey failed', e);
  }
}

export async function getSigningPrivateKey(userId: string): Promise<CryptoKey | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    return await new Promise<CryptoKey | null>((resolve) => {
      const req = store.get(`signing-private:${userId}`);
      req.onsuccess = () => resolve((req.result as CryptoKey) ?? null);
      req.onerror = () => resolve(null);
    });
  } catch (e) {
    console.warn('[signingKeyVault] getSigningPrivateKey failed', e);
    return null;
  }
}

export async function clearSigningPrivateKey(userId: string) {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(`signing-private:${userId}`);
    await awaitTx(tx);
  } catch (e) {
    console.warn('[signingKeyVault] clearSigningPrivateKey failed', e);
  }
}